import { useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import {
  isPlatformAuthenticatorAvailable,
  hasRegisteredCredential,
  verifyWithPasskey,
} from "../lib/webauthn";

const API = process.env.REACT_APP_BACKEND_URL;

// Tick once every 5 minutes while the user is in an "active operational
// mode" (Work Mode, an in-flight upload, an active lesson). Refresh-
// token life on the backend is 7 days, so as long as the user keeps
// ticking we silently mint a fresh access cookie via /api/auth/refresh.
const KEEP_ALIVE_INTERVAL_MS = 5 * 60 * 1000;

/**
 * useSessionKeepAlive(active, opts)
 *
 * Hook that quietly pings POST /api/auth/refresh every 5 minutes while
 * `active` is true and the user is logged in. Stops on manual logout
 * (auth-context.user goes null) or when `active` flips false.
 *
 * On two consecutive ticks failing it surfaces a single sticky toast:
 *   "Your session is about to expire. Tap to stay signed in."
 * Tapping triggers a fresh refresh attempt; if that ALSO fails the
 * user is sent to /login with a `returnTo` so they don't lose their
 * place after signing back in.
 *
 * `opts.label` is shown in console logs to make multi-mount tracing
 * easier (e.g. "work_mode" vs "lesson_review_upload").
 */
export function useSessionKeepAlive(active, opts = {}) {
  const { user } = useAuth();
  const consecutiveFailsRef = useRef(0);
  const warningShownRef = useRef(false);

  useEffect(() => {
    // Manual logout, navigation away, or "not active" all stop the
    // ticker. Do NOT keep pinging in the background just because the
    // user once mounted Work Mode — the spec is explicit about this.
    if (!active || !user) {
      consecutiveFailsRef.current = 0;
      warningShownRef.current = false;
      return undefined;
    }

    const ping = async ({ surfaceWarning = true } = {}) => {
      try {
        await axios.post(`${API}/api/auth/refresh`, {}, { withCredentials: true });
        consecutiveFailsRef.current = 0;
        warningShownRef.current = false;
      } catch (err) {
        consecutiveFailsRef.current += 1;
        if (
          surfaceWarning &&
          !warningShownRef.current &&
          consecutiveFailsRef.current >= 2
        ) {
          warningShownRef.current = true;
          // Decide which CTA to render — biometric (when supported AND
          // a credential is registered) or the standard refresh tap.
          // Probe BOTH defensively so a flaky network on the
          // has-credential check never blocks the warning toast.
          let canBiometric = false;
          try {
            const [supported, registered] = await Promise.all([
              isPlatformAuthenticatorAvailable(),
              hasRegisteredCredential(),
            ]);
            canBiometric = !!(supported && registered);
          } catch {
            canBiometric = false;
          }
          const onAction = async () => {
            try {
              if (canBiometric) {
                await verifyWithPasskey();           // biometric ceremony
              } else {
                await axios.post(`${API}/api/auth/refresh`, {}, { withCredentials: true });
              }
              toast.dismiss("session-keep-alive-warning");
              toast.success("Signed in — keep going");
              consecutiveFailsRef.current = 0;
              warningShownRef.current = false;
            } catch {
              // Final fallback — preserve the page so re-login
              // returns the user to where they were. Active uploads
              // and Work Mode state stay in localStorage / server-side
              // chunked_uploads — neither is wiped here.
              const here = window.location.pathname + window.location.search;
              window.location.assign(`/login?returnTo=${encodeURIComponent(here)}`);
            }
          };
          toast.warning(
            // May 14, 2026 — operator audit: title MUST NOT imply
            // imminent Face ID. Biometric only fires AFTER the user
            // taps the CTA — putting "Face ID check" in the title
            // misled users into thinking the native dialog had
            // already failed.
            "Session check needed — tap to keep going.",
            {
              duration: Infinity,
              id: "session-keep-alive-warning",
              action: {
                // The button label CAN reference biometric — tapping
                // genuinely starts the WebAuthn ceremony.
                label: canBiometric ? "Confirm with Face / Touch ID" : "Stay signed in",
                onClick: onAction,
              },
            },
          );
        }
      }
    };

    // Tick immediately so a session that was already close to expiry
    // gets refreshed before the next 5-minute window.
    ping({ surfaceWarning: false });
    const handle = window.setInterval(ping, KEEP_ALIVE_INTERVAL_MS);
    return () => {
      window.clearInterval(handle);
    };
  }, [active, user, opts.label]);
}

// Exported for tests / instrumentation.
useSessionKeepAlive.intervalMs = KEEP_ALIVE_INTERVAL_MS;
